<template>
    <el-popover
        placement="bottom"
        :width="popoverWidth"
        trigger="click"
        v-model="visible"
        @hide="handleHide"
        popper-class="table-filter-popper">
        <div class="filter-content">
            <!-- 搜索框 -->
            <div class="filter-search">
                <el-input 
                    v-model="searchText" 
                    placeholder="搜索..." 
                    size="mini" 
                    clearable
                    prefix-icon="el-icon-search">
                </el-input>
            </div>
            
            <!-- 选项列表 -->
            <el-scrollbar wrap-class="filter-scroll-wrap" :max-height="260">
                <el-checkbox-group v-model="tempSelected">
                    <el-checkbox 
                        v-for="opt in filteredOptions" 
                        :key="opt.value" 
                        :label="opt.value" 
                        class="filter-item">
                        {{ opt.text }}
                    </el-checkbox>
                </el-checkbox-group>
                <div v-if="filteredOptions.length === 0" class="no-data">
                    暂无匹配项
                </div>
            </el-scrollbar>
            
            <!-- 底部按钮 -->
            <div class="filter-bottom">
                <el-button size="mini" type="text" @click="handleReset">重置</el-button>
                <el-button size="mini" type="primary" @click="handleConfirm">确认</el-button>
            </div>
        </div>
        
        <span slot="reference" class="filter-trigger" :class="{ 'active': isFilterActive }">
            <i class="filter-icon"></i>
        </span>
    </el-popover>
</template>

<script>
export default {
    name: 'TableColumnFilter',
    props: {
        options: {
            type: Array,
            default: () => []
        },
        field: {
            type: String,
            required: true
        },
        value: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            visible: false,
            tempSelected: [],
            selected: this.value ? [...this.value] : [],
            isCancelled: false,
            searchText: ''
        };
    },
    computed: {
        isFilterActive() {
            return this.selected && this.selected.length > 0;
        },
        filteredOptions() {
            if (!this.searchText) {
                return this.options;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.options.filter(opt => 
                opt.text.toLowerCase().includes(searchLower)
            );
        },
        popoverWidth() {
            // 计算最长文本的宽度
            if (!this.options || this.options.length === 0) {
                return 200;
            }
            
            // 找出最长的文本
            let maxLength = 0;
            this.options.forEach(opt => {
                const length = this.getTextWidth(opt.text);
                if (length > maxLength) {
                    maxLength = length;
                }
            });
            
            // 基础宽度：checkbox(30px) + padding(24px) + scrollbar(10px) + 额外边距(20px)
            const baseWidth = 84;
            let calculatedWidth = maxLength + baseWidth;
            
            // 最小宽度 200px，最大宽度为屏幕宽度的一半
            const minWidth = 200;
            const maxWidth = window.innerWidth * 0.5;
            
            return Math.max(minWidth, Math.min(calculatedWidth, maxWidth));
        }
    },
    watch: {
        visible(val) {
            if (val) {
                this.tempSelected = [...this.selected];
                this.isCancelled = false;
                this.searchText = '';
            }
        },
        value(val) {
            this.selected = val ? [...val] : [];
        }
    },
    methods: {
        handleConfirm() {
            this.applyFilter();
            this.visible = false;
        },
        handleReset() {
            this.tempSelected = [];
            this.selected = [];
            this.$emit('filter-change', { field: this.field, values: [] });
            this.$emit('input', []);
            this.visible = false;
        },
        handleFilter(values) {
            this.tempSelected = [...values];
            this.selected = [...values];
            this.$emit('filter-change', { field: this.field, values: this.selected });
        },
        resetFilter() {
            this.tempSelected = [];
            this.selected = [];
        },
        handleHide() {
            if (!this.isCancelled) {
                this.applyFilter();
            }
        },
        applyFilter() {
            const isSame = this.tempSelected.length === this.selected.length && 
                           this.tempSelected.every(v => this.selected.includes(v));
            
            if (!isSame) {
                this.selected = [...this.tempSelected];
                this.$emit('filter-change', { field: this.field, values: this.selected });
                this.$emit('input', this.selected);
            }
        },
        getTextWidth(text) {
            // 创建临时 canvas 来测量文本宽度
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = '14px Arial, sans-serif';
            const metrics = context.measureText(text);
            return Math.ceil(metrics.width);
        }
    }
}
</script>

<style scoped>
.filter-content {
    padding: 8px 0;
}

.filter-search {
    padding: 0 12px 8px;
    border-bottom: 1px solid #ebeef5;
}

.filter-scroll-wrap {
    max-height: 260px;
    padding-top: 8px;
    overflow-y: auto;
}

.filter-scroll-wrap ::v-deep .el-scrollbar__wrap {
    overflow-x: hidden;
    overflow-y: auto;
}

.filter-item {
    display: block;
    margin-right: 0;
    margin-bottom: 8px;
    padding: 0 12px;
}

.no-data {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 20px 12px;
}

.filter-bottom {
    border-top: 1px solid #ebeef5;
    padding: 8px 8px 0;
    text-align: right;
    display: flex;
    justify-content: space-between;
}

.filter-trigger {
    cursor: pointer;
    display: inline-block;
    vertical-align: middle;
    margin-left: 5px;
    height: 14px;
    line-height: 1;
}

.filter-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: transparent;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M4 4h16l-7 7v5.2l-4 4v-9.2L4 4z' fill='none' stroke='%23616161' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}

.filter-trigger.active .filter-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M4 4h16l-7 7v5.2l-4 4v-9.2L4 4z' fill='%230A84FF' stroke='%23053D8C' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}
</style>

<style>
.table-filter-popper {
    max-height: 360px;
    overflow: hidden;
}

.table-filter-popper .filter-content {
    max-height: 360px;
    display: flex;
    flex-direction: column;
}

.table-filter-popper .filter-scroll-wrap {
    flex: 1;
    min-height: 0;
}
</style>
